import { useIntl, useSettings } from '@capsicum/common';
import { Accordion, Button } from '@capsicum/common-web';
import { useMemo } from 'react';
import styled from 'styled-components';

type SumSubConsentPageProps = {
	onAgree: () => void;
	onDismiss: () => void;
};

export const SumSubConsentPage = ({ onAgree, onDismiss }: SumSubConsentPageProps) => {
	const { $t } = useIntl();
	const {
		settings: { sumSubPolicyLink }
	} = useSettings();

	const accordionItems = useMemo(
		() => [
			{
				content: (
					<S.SectionDescription>
						{$t<JSX.Element>('proofOfId.consentPage.notes.one.description')}
					</S.SectionDescription>
				),
				id: '1',
				isExpanded: true,
				title: <S.SectionTitle>{$t('proofOfId.consentPage.notes.one.title')}</S.SectionTitle>
			},
			{
				content: (
					<S.SectionDescription>
						{$t<JSX.Element>('proofOfId.consentPage.notes.two.description', {
							br: <br />,
							link: chunks => (
								<S.Link href={sumSubPolicyLink} rel="noreferrer" target="_blank">
									{chunks}
								</S.Link>
							)
						})}
					</S.SectionDescription>
				),
				id: '2',
				isExpanded: false,
				title: <S.SectionTitle>{$t('proofOfId.consentPage.notes.two.title')}</S.SectionTitle>
			},
			{
				content: (
					<S.SectionDescription>
						{$t<JSX.Element>('proofOfId.consentPage.notes.three.description', {
							br: <br />,
							link: chunks => (
								<S.Link href={sumSubPolicyLink} rel="noreferrer" target="_blank">
									{chunks}
								</S.Link>
							)
						})}
					</S.SectionDescription>
				),
				id: '3',
				isExpanded: false,
				title: <S.SectionTitle>{$t('proofOfId.consentPage.notes.three.title')}</S.SectionTitle>
			},
			{
				content: (
					<S.SectionDescription>
						{$t<JSX.Element>('proofOfId.consentPage.notes.four.description', {
							br: <br />,
							link: chunks => (
								<S.Link href={sumSubPolicyLink} rel="noreferrer" target="_blank">
									{chunks}
								</S.Link>
							)
						})}
					</S.SectionDescription>
				),
				id: '4',
				isExpanded: false,
				title: <S.SectionTitle>{$t('proofOfId.consentPage.notes.four.title')}</S.SectionTitle>
			}
		],
		[$t]
	);

	return (
		<>
			<S.Wrapper>
				<S.Header>{$t('proofOfId.consentPage.consentHeader')}</S.Header>
				<S.Description>
					{$t<JSX.Element>('proofOfId.consentPage.consentDescription', {
						br: <br />,
						link: chunks => (
							<S.Link href={sumSubPolicyLink} rel="noreferrer" target="_blank">
								{chunks}
							</S.Link>
						)
					})}
				</S.Description>
				<S.AccordionWrapper>
					<Accordion items={accordionItems} />
				</S.AccordionWrapper>
			</S.Wrapper>
			<S.ButtonsWrapper>
				<S.Buttons>
					<Button
						fullWidth
						key="dismiss"
						testID="proofOfId-consent-page-button-dismiss"
						text={$t('proofOfId.consentPage.buttonDisagree')}
						variant="outlined"
						onClick={onDismiss}
					/>
					<Button
						fullWidth
						key="agree"
						testID="proofOfId-consent-page-button-agree"
						text={$t('proofOfId.consentPage.buttonAgree')}
						onClick={onAgree}
					/>
				</S.Buttons>
			</S.ButtonsWrapper>
		</>
	);
};

namespace S {
	export const Wrapper = styled.div`
		display: flex;
		flex-direction: column;
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const ButtonsWrapper = styled.div`
		display: flex;
		position: fixed;
		bottom: 0;
		left: 0;
		right: 0;
		padding: ${({ theme }) => theme.spacings.small};
		background-color: ${({ theme }) => theme.colors.backgrounds.main.container};
	`;

	export const Buttons = styled.div`
		display: flex;
		flex-direction: row;
		justify-content: center;
		align-items: center;
		gap: ${({ theme }) => theme.spacings.xsmall};
		width: 100%;

		${({ theme }) => theme.breakpoints.queries.above.tabletSmallDesktop} {
			width: ${({ theme }) => `${theme.breakpoints.points.mediumDesktop.subpageWidth}px`};
			margin: 0 auto;
		}
	`;

	export const AccordionWrapper = styled.div`
		flex: 1;
		display: flex;
		flex-direction: column;
	`;

	export const Link = styled.a`
		color: ${({ theme }) => theme.colors.text.brand.enabled};
	`;

	export const Header = styled.h2`
		font-weight: 700;
		padding: ${({ theme }) => `0 ${theme.spacings.small}`};
		${({ theme }) => theme.fonts.text.text9};
	`;

	export const Description = styled.p`
		padding: ${({ theme }) => `0 ${theme.spacings.small}`};
		${({ theme }) => theme.fonts.text.text3};
	`;

	export const SectionTitle = styled.h2`
		${({ theme }) => theme.fonts.text.textTopBar};
		margin-bottom: ${({ theme }) => theme.spacings.xxxsmall};
	`;

	export const SectionDescription = styled.p`
		${({ theme }) => theme.fonts.text.text3};
		margin-bottom: ${({ theme }) => theme.spacings.small};
		text-align: justify;
	`;
}
